import type {
  TApplicationStatus,
  TListApplicationCardsInputDTO,
} from '@doutorsim/shared/applications'
import type { TFindUsersInputDTO } from '@doutorsim/shared/users'
import { createQueryKeyStore } from '@lukemorales/query-key-factory'

import { api } from '../api'
import type { GetApplicationsParams } from '../api/entities/applications'
import { unwrapApiResult } from './helpers'

export const queries = createQueryKeyStore({
  banks: {
    list: {
      queryKey: null,
      queryFn: async () => unwrapApiResult(await api.banks.list()),
      // Banks are immutable reference data, so never treat the cache as stale.
      staleTime: Number.POSITIVE_INFINITY,
    },
  },

  attachments: {
    list: (companyId: string, facilityId: string, applicationId: string) => ({
      queryKey: [companyId, facilityId, applicationId],
      queryFn: async () => unwrapApiResult(await api.attachments.list(applicationId)),
    }),
  },

  applications: {
    list: (companyId: string, facilityId: string, params?: GetApplicationsParams) => ({
      queryKey: [companyId, facilityId, params],
      queryFn: () => api.applications.getApplications(companyId, facilityId, params),
    }),
    detail: (companyId: string, facilityId: string, id: string) => ({
      queryKey: [companyId, facilityId, id],
      queryFn: async () =>
        unwrapApiResult(await api.applications.getApplicationById(id, companyId, facilityId)),
    }),
    detailFull: (companyId: string, facilityId: string, id: string) => ({
      queryKey: [companyId, facilityId, id, 'full'],
      queryFn: async () => {
        const application = unwrapApiResult(
          await api.applications.getApplicationById(id, companyId, facilityId),
        )
        const facility = await api.companies.facilities
          .get(application.companyId, application.facilityId)
          .then(unwrapApiResult)
        return { application, facility }
      },
    }),
    statusHistory: (applicationId: string, companyId: string, facilityId: string) => ({
      queryKey: [applicationId, companyId, facilityId],
      queryFn: () =>
        api.applications.getApplicationStatusHistory({ applicationId, companyId, facilityId }),
    }),
  },

  persons: {
    list: (companyId: string) => ({
      queryKey: [companyId],
      queryFn: async () => unwrapApiResult(await api.persons.getPersons()),
    }),
    detail: (companyId: string, personId: string) => ({
      queryKey: [companyId, personId],
      queryFn: async () => unwrapApiResult(await api.persons.getPersonById(personId, companyId)),
    }),
  },

  companies: {
    list: {
      queryKey: null,
      queryFn: async () => unwrapApiResult(await api.companies.list()),
    },
    detail: (companyId: string) => ({
      queryKey: [companyId],
      queryFn: async () => unwrapApiResult(await api.companies.get(companyId)),
    }),
    bySlug: (slug: string) => ({
      queryKey: [{ slug }],
      queryFn: async () => unwrapApiResult(await api.companies.getBySlug(slug)),
    }),
    facilities: (companyId: string) => ({
      queryKey: [companyId],
      queryFn: async () => unwrapApiResult(await api.companies.facilities.list(companyId)),
    }),
    facility: (companyId: string, facilityId: string) => ({
      queryKey: [companyId, facilityId],
      queryFn: async () =>
        unwrapApiResult(await api.companies.facilities.get(companyId, facilityId)),
    }),
    facilityBySlug: (companyId: string, slug: string) => ({
      queryKey: [companyId, { slug }],
      queryFn: async () =>
        unwrapApiResult(await api.companies.facilities.getBySlug(companyId, slug)),
    }),
  },

  users: {
    list: (params: TFindUsersInputDTO) => ({
      queryKey: [params],
      queryFn: async () => unwrapApiResult(await api.users.list(params)),
    }),
  },

  companyApplications: {
    list: (companyId: string, params?: GetApplicationsParams) => ({
      queryKey: [companyId, params],
      queryFn: () => api.companyApplications.getApplications(companyId, params),
    }),
  },

  staffApplications: {
    list: (params?: GetApplicationsParams) => ({
      queryKey: [params],
      queryFn: () => api.staff.applications.getApplications(params),
    }),
  },

  staffApplicationCards: {
    all: (params?: TListApplicationCardsInputDTO) => ({
      queryKey: [params],
      queryFn: () => api.staff.applications.getApplicationCards(params),
    }),
    byStatus: (status: TApplicationStatus, params?: GetApplicationsParams) => ({
      queryKey: [status, params],
      queryFn: () => api.staff.applications.getApplicationCardsByStatus(status, params),
    }),
    declarations: (applicationId: string) => ({
      queryKey: [applicationId, 'declarations'],
      queryFn: () => api.staff.applications.getPersonDeclarations(applicationId),
    }),
    dataConfirmation: (applicationId: string) => ({
      queryKey: [applicationId, 'data-confirmation'],
      queryFn: () => api.staff.applications.getDataConfirmation(applicationId),
    }),
  },

  staffCompanies: {
    list: {
      queryKey: null,
      queryFn: async () => unwrapApiResult(await api.staff.companies.list()),
    },
  },

  staffFacilities: {
    list: {
      queryKey: null,
      queryFn: async () => unwrapApiResult(await api.staff.facilities.list()),
    },
  },

  staffPersons: {
    detail: (companyId: string, personId: string) => ({
      queryKey: [companyId, personId],
      queryFn: async () => unwrapApiResult(await api.staff.persons.get(companyId, personId)),
    }),
    officialData: (companyId: string, personId: string) => ({
      queryKey: [companyId, personId, 'official-data'],
      queryFn: () => api.staff.persons.getOfficialData(companyId, personId),
    }),
  },
})
